import idx from "idx";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "../scss/project-management.scss";
import { Paper } from "@material-ui/core";
import OverviewDetails from "../overview-details";
import ProjectSummary from "../project-summary";
import ProjectMenu from "../project-menu";
import { Button } from "@mui/material";
import TaskList from "../task-list";
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import { getProjectSummaryData } from "../utils/get-project-summary";
import { resolveProjectType } from "../utils/resolve-project-type";
import { editProject } from "../../../actions/project-management.action";
import { updateProjectAction, clearProjectAction } from "../../../slice/project-summary";
import { ProjectDataLoader } from "../../../components/loading";

/**
 * Main project overview page displaying project cards, financial summaries, and task lists.
 *
 * @param {Object} props - Component properties.
 * @returns {JSX.Element} The rendered project overview component.
 */
export default function ViewProject(props) {

    const [viewProjectState, setViewProjectState] = useState({
        anchorEl: null,
        open: false,
        customstyle: "",
        statusLoading: false,
        pageLoading: false,
        error: null,
        isFadingOut: false,
    })

    const { projectId } = useParams();

    const dispatch = useDispatch()
    const navigate = useNavigate();
    const location = useLocation();
    const projectSummary = useSelector(state => state.projectSummary) || {}
    const projectCard = useSelector(state => state.projectSummary.projectcard) || {}
    const progressCard = useSelector(state => state.projectSummary.progresscard) || {}
    const financialCard = useSelector(state => state.projectSummary.financialCard) || {}
    const overviewCards = useSelector(state => state.projectSummary.overviewCards) || {}
    const projectState = useSelector(state => state.project);

    useEffect(() => {
        if (!projectId) return;
        
        const currentLoadedId = projectSummary?.project?.res?.id || projectSummary?.project?.projectId;
        if (currentLoadedId && currentLoadedId !== projectId) {
            dispatch(clearProjectAction());
        }

        setViewProjectState(prev => ({ 
            ...prev, 
            pageLoading: true, 
            error: null 
        }));
        
        const fetchData = async () => {
            try {
                await getProjectSummaryData({ projectId, dispatch, type: 'project-view', forceFresh: true });
            } catch (error) {
                setViewProjectState(prev => ({ 
                    ...prev, 
                    error: error.message || 'Failed to load project data' 
                }));
            } finally {
                // Start fade-out animation before hiding loading state
                setViewProjectState(prev => ({ 
                    ...prev, 
                    isFadingOut: true 
                }));
                
                // Wait for fade-out animation to complete before hiding
                setTimeout(() => {
                    setViewProjectState(prev => ({ 
                        ...prev, 
                        pageLoading: false,
                        isFadingOut: false 
                    }));
                }, 300);
            }
        };
        
        fetchData();
    }, [projectId, dispatch]);

    useEffect(() => {
        if (idx(location, _ => _.state.openUploadCostModal)) {
            setViewProjectState(prev => ({
                ...prev,
                anchorEl: true
            }));
            navigate(location.pathname + location.search, { replace: true, state: {} });
        }
    }, [location.state, navigate, location.pathname, location.search]);


    const handleMenu = () => {
        setViewProjectState(prev => ({
            ...prev,
            anchorEl: true
        }));
    };

    // const ViewrfqDialogHandler = () => {
    //     navigate(`${props.moduleURL}/rfqview/${viewProjectState.projectId}`);
    // };

    const handleClose = () => {
        setViewProjectState(prev => ({
            ...prev,
            anchorEl: null, hasStages: null
        }));
    };
    const viewProjectList = () => {
        let type = resolveProjectType(
            idx(projectSummary, _ => _.project.projectDataType) || idx(projectSummary, _ => _.project.res.projectType),
            projectState
        );
        navigate(`/projects?project-type=${type}`);
    };

    const handleStatusChange = async (newStatus) => {
        setViewProjectState(prev => ({ ...prev, statusLoading: true }))
        try {
            await dispatch(editProject({
                status: newStatus
            }, projectId))
            dispatch(updateProjectAction({ status: newStatus }))
        } finally {
            setViewProjectState(prev => ({ ...prev, statusLoading: false }))
        }
    }

    // Show loading state or error state using reusable loader
    if (viewProjectState.pageLoading || viewProjectState.isFadingOut || viewProjectState.error) {
        return (
            <ProjectDataLoader
                loading={viewProjectState.pageLoading}
                isFadingOut={viewProjectState.isFadingOut}
                error={viewProjectState.error}
            />
        );
    }

    return (
        <div className="viewProject viewProject-ch">
            {Boolean(viewProjectState.anchorEl) && <ProjectMenu
                open={Boolean(viewProjectState.anchorEl)}
                anchorEl={viewProjectState.anchorEl}
                projectId={projectId}
                type={idx(projectSummary, _ => _.project.projectType)}
                dlp={idx(projectSummary, _ => _.project.dlp)}
                completed={idx(projectSummary, _ => _.project.completed)}
                lost={idx(projectSummary, _ => _.project.lost)}
                archived={idx(projectSummary, _ => _.project.archived)}
                projectType={idx(projectSummary, _ => _.project.projectDataType)}
                writePermission={props.updatePermission}
                history={navigate}
                onClose={handleClose}
                hasStages={idx(projectSummary, _ => _.project.hasStages)}
                menuType={"View"}
                status={idx(projectSummary, _ => _.project.status)}
                projectName={idx(projectSummary, _ => _.project.name)}
                customstyle={"project-view-menuStyle"}
            />}

            <div className="row">
                <div className="w-auto">
                    <div className="col-12 breadcrumb-block p-0">
                        <Breadcrumb>
                            <Breadcrumb.Item onClick={viewProjectList}>PROJECTS</Breadcrumb.Item>
                            <Breadcrumb.Item active>{idx(projectSummary, _ => _.project.projectName)}</Breadcrumb.Item>
                        </Breadcrumb>
                    </div>
                </div>
                <div className="w-auto ms-auto mt-auto">
                    <Button
                        variant="outlined"
                        onClick={handleMenu}
                        aria-controls="simple-menu"
                        aria-haspopup="true"
                    >
                        ...
                    </Button>
                </div>
            </div>

            <div className="view-project-page">
                {/* ---------------------progress financial---------------------------- */}
                <div className="mobileViewProject" style={{ background: "transparent" }}>
                    <Paper>
                        <ProjectSummary
                            projectId={projectId}
                            projectName={idx(projectCard, _ => _.projectName) || ""}
                            siteAddress={idx(projectCard, _ => _.addressBreakdown.formatted_address) || ""}
                            customerName={idx(projectCard, _ => _.customer) || ""}
                            contactData={idx(projectCard, _ => _.contact) || ""}
                            projectManagerName={idx(projectCard, _ => _.assignedTo) || ""}
                            status={idx(projectSummary, _ => _.project.status)}
                            onStatusChange={handleStatusChange}
                            statusLoading={viewProjectState.statusLoading}
                            quoteType={idx(projectSummary, _ => _.project.quoteType)}
                            progressCostMaterial={idx(progressCard, _ => _.remainingMaterialBudget)}
                            progressCostLabor={idx(progressCard, _ => _.remainingResourceBudget)}
                            progressCostSubcontractor={idx(progressCard, _ => _.remainingContractorBudget)}
                            materialAllowance={idx(progressCard, _ => _.materialAllowance)}
                            resourceAllowance={idx(progressCard, _ => _.resourceAllowance)}
                            contractorAllowance={idx(progressCard, _ => _.contractorAllowance)}
                            materialActualSpending={idx(progressCard, _ => _.materialActualSpending)}
                            resourceActualSpending={idx(progressCard, _ => _.resourceActualSpending)}
                            contractorActualSpending={idx(progressCard, _ => _.contractorActualSpending)}
                            timeSpend={idx(progressCard, _ => _.timeSpend)}
                            timeAllocated={idx(progressCard, _ => _.timeAllocated)}
                            totalContract={idx(financialCard, _ => _.contract)}
                            totalVariationAmount={idx(financialCard, _ => _.variations)}
                            totalRevisedContractCost={idx(financialCard, _ => _.revisedContract)}
                            totalClaimAmount={idx(financialCard, _ => _.claimed)}
                            totalRetentionAmount={idx(financialCard, _ => _.retentionHeld)}
                            balanceAmount={idx(financialCard, _ => _.balance)}
                        />
                    </Paper>
                </div>
                {/* ----------------------task cards-------------------------- */}
                <div className="mobileViewProject" style={{ background: "transparent" }}>
                    <Paper className="main" style={{ boxShadow: "none", background: "transparent" }}>
                        <OverviewDetails
                            // handleRFQClick={ViewrfqDialogHandler}
                            totalRFQSend={idx(overviewCards, _ => _.totalRFQSend)}
                            totalVariation={idx(overviewCards, _ => _.variationCount)}
                            // handlePoClick={poDialogHandler}
                            projectId={projectId}
                            quoteType={idx(projectSummary, _ => _.project.quoteType)}
                            progressClaimCount={idx(overviewCards, _ => _.budgetCount)}
                            purchaseOrdersCount={idx(overviewCards, _ => _.purchaseOrderCount)}
                            workOrdersCount={idx(overviewCards, _ => _.workOrderCount)}
                            invoicesCount={idx(overviewCards, _ => _.invoiceCount)}
                            vendorInvoiceCount={idx(overviewCards, _ => _.vendorInvoiceCount)}
                            financePermission={props.financePermission}
                            rFinancePermission={props.rFinancePermission}
                            rSupplierPermission={props.rSupplierPermission}
                            wSupplierPermission={props.wSupplierPermission}
                            rContractorPermission={props.rContractorPermission}
                            wContractorPermission={props.wContractorPermission}
                            projectName={idx(projectSummary, _ => _.project.projectName)}
                            projectType={idx(projectSummary, _ => _.project.projectDataType)}
                            projectStatus={idx(projectSummary, _ => _.project.status)}
                        />
                    </Paper>
                </div>

                {/* ---------------------table--------------------------- */}
                <div className="table-responsive tender-table">
                    <TaskList
                        {...props}
                        projectId={projectId}
                        type="project-view"
                    />
                </div>
            </div>
        </div>
    );
}
